import React, { useState } from 'react';
import { OutgoingMembershipProductDto } from 'api/dtos/memberships/products';
import { currencyFormat } from 'legacy/common/utils/currency';
import { useTranslation } from 'react-i18next';
import useProductTimeGateText from 'hooks/useProductTimeGateText';
import Toggle from 'legacy/common/components/form/Toggle';
import { useUpdateOrganizationMembership } from 'api/queries/organizations/memberships';
import { toast } from 'react-hot-toast';
import { IoMdPause } from 'react-icons/io';
import { FaRegEyeSlash } from 'react-icons/fa';

const MembershipTierCard = ({
  tier,
  onClick
}: {
  tier: OutgoingMembershipProductDto;
  onClick: (membershipTierId: string) => void;
}) => {
  const [isActive, setIsActive] = useState(tier.product.isActive);
  const [isVisible, setIsVisible] = useState(tier.product.isVisible);
  const { t } = useTranslation(['organizations']);
  const timeGateText = useProductTimeGateText({
    validFrom: tier.validFrom,
    validUntil: tier.validUntil
  });

  const { mutate: updateMembershipTier } = useUpdateOrganizationMembership();

  const handleClick = () => {
    onClick(tier.product.id);
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleClick();
    }
  };

  const handleTogglePauseTierSales = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const newActiveState = !isActive;
    setIsActive(newActiveState);

    updateMembershipTier(
      {
        organizationId: tier.access.id,
        membershipTierId: tier.product.id,
        membershipTier: {
          product: { isActive: newActiveState }
        }
      },
      {
        onError: () => {
          toast.error(
            t('organizations:memberships.membershipsTiers.pauseTierSalesError')
          );
          setIsActive(isActive);
        }
      }
    );
  };

  const handleToggleHideTier = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const newVisibleState = !isVisible;

    setIsVisible(newVisibleState);

    updateMembershipTier(
      {
        organizationId: tier.access.id,
        membershipTierId: tier.product.id,
        membershipTier: {
          product: { isVisible: newVisibleState }
        }
      },
      {
        onError: () => {
          toast.error(
            t('organizations:memberships.membershipsTiers.hideTierError')
          );
          setIsVisible(isVisible);
        }
      }
    );
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="w-full justify-between relative cursor-pointer border-1 rounded-2xl p-4 border-gray-200 hover:border-gray-500"
      onClick={handleClick}
      onKeyDown={handleOnKeyDown}
    >
      <div className="p-4">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <div className="font-bold text-base">{tier.product.name}</div>
          </div>
        </div>
        <div className="flex flex-col pt-2 pb-4">
          <div className="text-base font-bold text-[#202020]">
            {currencyFormat(tier.product.price)}{' '}
            {t('memberships.membershipsTiers.oneTimeCharge')}
          </div>
          <div className="flex flex-row items-center">
            <div className="text-base font-normal text-[#202020]">
              {timeGateText}
            </div>
          </div>
        </div>
        <div className="flex flex-col pt-2 pb-4">
          <div className="text-base font-normal text-[#202020]">
            {tier.product.description}
          </div>
        </div>
      </div>
      <div className="mt-4 w-full border-t border-[#EBEBEB]" />
      <div className="mt-4 flex flex-col gap-3 p-4">
        <div className="flex flex-row justify-between items-center ">
          <div className="flex gap-3">
            <IoMdPause size={18} />
            <span className="text-sm font-normal text-[#202020]">
              {t('organizations:memberships.membershipsTiers.pauseTierSales')}
            </span>
          </div>
          <div className="ml-2">
            <Toggle isActive={!isActive} onClick={handleTogglePauseTierSales} />
          </div>
        </div>
        <div className="flex flex-row items-center justify-between">
          <div className="flex gap-2">
            <FaRegEyeSlash size={18} />
            <span className="text-sm font-normal text-[#202020]">
              {t('organizations:memberships.membershipsTiers.hideTier')}
            </span>
          </div>
          <div className="ml-2">
            <Toggle isActive={!isVisible} onClick={handleToggleHideTier} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipTierCard;
